import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const StarIcon = ({ filled }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? "#f5a623" : "none"}
    stroke="#f5a623" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Avatar with fallback — handles CORS by catching img error
const Avatar = ({ src, name }) => {
  const [failed, setFailed] = useState(false);
  const initial = (name || "A").charAt(0).toUpperCase();

  // Generate a consistent color from name
  const colors = ["#1a1a2e","#16213e","#0f3460","#533483","#2b2d42","#3d405b","#1b4332","#1a3a4a"];
  const colorIndex = (name || "").charCodeAt(0) % colors.length;
  const bg = colors[colorIndex];

  if (!src || failed) {
    return (
      <div style={{ ...styles.avatarFallback, background: bg }}>
        {initial}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name || "User"}
      referrerPolicy="no-referrer"          // ← key fix for Google photos
      crossOrigin="anonymous"
      style={styles.avatar}
      onError={() => setFailed(true)}
    />
  );
};

const Testimonials = (props) => {
  const [expandedReviews, setExpandedReviews] = useState({});

  useEffect(() => {
    props.fetchReviews();
  }, []);

  const handleToggleReadMore = (index) => {
    setExpandedReviews((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const truncateText = (text, wordLimit) => {
    const words = (text || "").split(" ");
    if (words.length > wordLimit) {
      return {
        truncated: words.slice(0, wordLimit).join(" "),
        remaining: words.slice(wordLimit).join(" "),
      };
    }
    return { truncated: text, remaining: "" };
  };

  const filteredReviews = (props.client.all_reviews || []).filter(
    (review) => review.rating >= 4
  );

  // Average rating
  const avgRating = filteredReviews.length
    ? (filteredReviews.reduce((s, r) => s + r.rating, 0) / filteredReviews.length).toFixed(1)
    : "5.0";

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <StarIcon key={i} filled={i < Math.round(rating)} />
    ));

  return (
    <section style={styles.section}>
      {/* Header */}
      <div style={styles.header}>
        <span style={styles.eyebrow}>Google Reviews</span>
        <h2 style={styles.heading}>What Our Customers Say</h2>
        <div style={styles.ratingRow}>
          <div style={styles.starsRow}>{renderStars(5)}</div>
          <span style={styles.avgNum}>{avgRating}</span>
          <span style={styles.reviewCount}>({filteredReviews.length}+ reviews)</span>
        </div>
      </div>

      {/* Swiper */}
      <Swiper
        modules={[ Pagination, Autoplay]}
        navigation
        pagination={{ clickable: true }}
        autoplay={{ delay: 3800, disableOnInteraction: false }}
        spaceBetween={20}
        breakpoints={{
          0:    { slidesPerView: 1 },
          640:  { slidesPerView: 2 },
          980:  { slidesPerView: 3 },
          1280: { slidesPerView: 4 },
        }}
        style={{ padding: "6px 4px 52px" }}
      >
        {filteredReviews.map((item, index) => {
          const { truncated, remaining } = truncateText(item.text || "", 40);
          const isExpanded = expandedReviews[index];

          return (
            <SwiperSlide key={index} style={{ height: "auto" }}>
              <div style={styles.card}>

                {/* Card top: Google + stars */}
                <div style={styles.cardTop}>
                  <GoogleIcon />
                  <div style={styles.starsRow}>
                    {renderStars(item.rating)}
                  </div>
                  <span style={styles.ratingBadge}>{item.rating}.0</span>
                </div>

                {/* Review text */}
                <p style={styles.reviewText}>
                  &ldquo;{isExpanded
                    ? `${truncated} ${remaining}`
                    : `${truncated}${remaining ? "…" : ""}`
                  }&rdquo;
                </p>

                {remaining && (
                  <button style={styles.readMoreBtn} onClick={() => handleToggleReadMore(index)}>
                    {isExpanded ? "Show less ↑" : "Read more ↓"}
                  </button>
                )}

                {/* Time */}
                {item.relative_time_description && (
                  <span style={styles.timeLabel}>{item.relative_time_description}</span>
                )}

                {/* Author */}
                <div style={styles.author}>
                  <Avatar src={item.profile_photo_url} name={item.author_name} />
                  <div>
                    <div style={styles.authorName}>{item.author_name || "Anonymous"}</div>
                    <div style={styles.authorSub}>Verified Google Review</div>
                  </div>
                </div>

              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </section>
  );
};

const styles = {
  section: {
    padding: "72px 4vw 48px",
    background: "#f9f9f7",
    fontFamily: "'KIAbodyLatinbasic_Regular', sans-serif",
    overflowX: "hidden",
  },
  header: {
    textAlign: "center",
    marginBottom: "44px",
  },
  eyebrow: {
    display: "inline-block",
    fontSize: "0.68rem",
    letterSpacing: "3px",
    textTransform: "uppercase",
    color: "#aaa",
    fontFamily: "'KIAbodyLatinbasic_Bold', sans-serif",
    marginBottom: "12px",
  },
  heading: {
    fontFamily: "'KIAbodyLatinbasic_Bold', sans-serif",
    fontSize: "clamp(1.5rem, 3vw, 2.2rem)",
    fontWeight: 800,
    color: "#111",
    margin: "0 0 16px",
    lineHeight: 1.2,
  },
  ratingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  starsRow: {
    display: "flex",
    gap: "3px",
    alignItems: "center",
  },
  avgNum: {
    fontFamily: "'KIAbodyLatinbasic_Bold', sans-serif",
    fontSize: "1rem",
    color: "#f5a623",
    fontWeight: 800,
  },
  reviewCount: {
    fontSize: "0.82rem",
    color: "#aaa",
  },

  // Card
  card: {
    background: "#ffffff",
    border: "1px solid #ebebeb",
    borderRadius: "2px",
    padding: "22px 20px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    height: "100%",
    boxSizing: "border-box",
    transition: "box-shadow 0.25s, transform 0.25s",
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  ratingBadge: {
    marginLeft: "auto",
    fontFamily: "'KIAbodyLatinbasic_Bold', sans-serif",
    fontSize: "0.75rem",
    color: "#f5a623",
    fontWeight: 700,
    background: "#fff8ee",
    border: "1px solid #fde8b0",
    borderRadius: "99px",
    padding: "2px 8px",
  },
  reviewText: {
    fontSize: "0.82rem",
    color: "#555",
    lineHeight: 1.75,
    flex: 1,
    fontStyle: "italic",
    margin: 0,
  },
  readMoreBtn: {
    background: "none",
    border: "none",
    color: "#111",
    fontSize: "0.75rem",
    fontFamily: "'KIAbodyLatinbasic_Bold', sans-serif",
    cursor: "pointer",
    padding: 0,
    textAlign: "left",
    letterSpacing: "0.03em",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
  },
  timeLabel: {
    fontSize: "0.7rem",
    color: "#ccc",
    letterSpacing: "0.5px",
  },
  author: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "auto",
    paddingTop: "14px",
    borderTop: "1px solid #f4f4f2",
  },
  avatar: {
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
    display: "block",
  },
  avatarFallback: {
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontFamily: "'KIAbodyLatinbasic_Bold', sans-serif",
    fontWeight: 700,
    flexShrink: 0,
  },
  authorName: {
    fontSize: "0.82rem",
    fontFamily: "'KIAbodyLatinbasic_Bold', sans-serif",
    fontWeight: 700,
    color: "#111",
    lineHeight: 1.3,
  },
  authorSub: {
    fontSize: "0.68rem",
    color: "#bbb",
    letterSpacing: "0.3px",
    marginTop: "2px",
  },
};

export default Testimonials;